import { API_BASE_URL } from "@/lib/constants";
import { getSession } from "next-auth/react";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly responseText: string
  ) {
    super(`API error ${status}: ${responseText}`);
    this.name = "ApiError";
  }
}

function readCookieToken(): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("app-token="))
    ?.split("=")[1];
}

function clearCookieToken() {
  document.cookie = "app-token=; Path=/; Max-Age=0; SameSite=Lax";
}

type TokenState =
  | { token: string; missingAppToken: false }
  | { token: undefined; missingAppToken: boolean };

async function getTokenState(): Promise<TokenState> {
  if (typeof window !== "undefined") {
    try {
      const session = await getSession();
      const sessionToken = session?.appToken;
      if (sessionToken) {
        document.cookie = `app-token=${encodeURIComponent(sessionToken)}; Path=/; SameSite=Lax`;
        return { token: sessionToken, missingAppToken: false };
      }

      clearCookieToken();
      return { token: undefined, missingAppToken: Boolean(session?.user) };
    } catch {
      // If session fetch fails (for example /api/auth/session 500), fall back to last known token.
      const cookieToken = readCookieToken();
      if (cookieToken) {
        return { token: decodeURIComponent(cookieToken), missingAppToken: false };
      }
    }
  }
  return { token: undefined, missingAppToken: false };
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  async function performRequest(token: string | undefined) {
    const headers: HeadersInit = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };
    return fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  }

  const { token, missingAppToken } = await getTokenState();
  if (!token && missingAppToken) {
    if (typeof window !== "undefined") {
      window.location.assign("/login");
    }
    throw new ApiError(401, "Missing app token");
  }

  let res = await performRequest(token);
  if (res.status === 401 && token) {
    clearCookieToken();
    const refreshed = await getTokenState();
    if (refreshed.token && refreshed.token !== token) {
      res = await performRequest(refreshed.token);
    }
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}
